import * as React from "react";
import * as moment from "moment";
import { connect } from "react-redux";
import { Button, Modal, MultiSelect } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { cloneDeep } from "lodash";
import Select from "react-select";

import APIClient from "web_modules/APIClient";
import LearnMore from "web_modules/shared/LearnMore";
import { AppShape } from "@eave/shared/types/api";
import {
  loanDecisions,
  loanDecisionToStr,
  SuspendedReasons,
  SuspendedReasonsDisplayValues,
  WithdrawnReasons,
  WithdrawnReasonsDisplayValues,
} from "web_modules/shared/loan_decisions";
import { loanStatusToStr } from "web_modules/shared/loan_statuses";
import { visibleLoanStatusOptions } from "./helpers";
import { mapDispatchToProps } from "../selectors";
import { Column, TRUSTable } from "../../../shared/TRUSTable";
import Contact from "../../shared/Contact";

const styles = require("./styles.module.scss");

const EMDASH = <span className={styles.emdash}>—</span>;

interface SummaryProps {
  app: AppShape;
  loadApp: Function;
  dispatch: Function;
}

const withdrawnReasonItems = Object.keys(WithdrawnReasons).map(s => ({
  id: WithdrawnReasons[s],
  value: `${WithdrawnReasonsDisplayValues[WithdrawnReasons[s]]}`,
}));

const suspendedReasonItems = Object.keys(SuspendedReasons).map(s => ({
  id: SuspendedReasons[s],
  value: `${SuspendedReasonsDisplayValues[SuspendedReasons[s]]}`,
}));

class Summary extends React.Component<SummaryProps, any> {
  constructor(props) {
    super(props);
    this.state = Summary.initialState(props);
    this.setStateForContactFields = this.setStateForContactFields.bind(this);
  }

  componentDidMount() {
    this.loadUnderwritingSummaries();
    this.getEaveTeamOptions();
  }

  static initialState(props) {
    const { underwriting_summary } = props.app;

    const state = {
      hasUnsavedChanges: false,
      isLoading: true,
      summaries: null,
      contacts: props.app.contacts,
      current: {
        loan_status: null,
        loan_decision: null,
        loan_details: null,
        reasons: null,
      },
      errorMessage: null,
    };

    if (underwriting_summary) {
      state.current = {
        loan_status: underwriting_summary.loan_status,
        loan_decision: underwriting_summary.loan_decision,
        loan_details: underwriting_summary.loan_details,
        reasons: underwriting_summary.reasons,
      };
    }

    return state;
  }

  // prettier-ignore
  endpoint = () =>
      `/loan_applications/${this.props.app.id}/hurdles/underwriting_summary`

  loadUnderwritingSummaries() {
    APIClient.get(this.endpoint()).then((resp: any) => {
      this.setState({
        ...this.state,
        isLoading: false,
        summaries: resp.summaries,
      });
    });
  }

  onSave() {
    const { app, loadApp } = this.props;
    const { contacts } = this.state;

    const updatedUnderwriter = {
      ...contacts.loan_underwriter,
      // source: "UnderwritingSummaryHurdle",
      source: "ContactInfoHurdle",
      loan_application_id: app.id,
    };

    const whenUnderwriterUpdates = APIClient.post(`/contacts`, updatedUnderwriter);
    const whenLoanInfoUpdates = APIClient.put(this.endpoint(), this.state.current);

    Promise.all([whenLoanInfoUpdates, whenUnderwriterUpdates])
      .then(() => {
        loadApp(app.id);
      })
      .catch(resp => {
        if (resp?.response?.error) {
          this.setState({ errorMessage: resp?.response?.error });
        }
      });
  }

  onChangeSelect(currentAttr, e) {
    this.setState({
      ...this.state,
      hasUnsavedChanges: true,
      current: {
        ...this.state.current,
        [currentAttr]: e ? e.value : null,
      },
    });
  }

  onChangeText(e) {
    const { value } = e.target;

    let val;
    if (!value || value === "") {
      val = null;
    } else {
      val = value;
    }

    this.setState({
      ...this.state,
      hasUnsavedChanges: true,
      current: {
        ...this.state.current,
        loan_details: val,
      },
    });
  }

  getEaveTeamOptions() {
    const { app } = this.props;
    APIClient.get(`/uw/${app.id}/contacts/options`, response => {
      this.setState({ eave_team_options: response });
    });
  }

  setStateForContactFields(contact_role_enum, role, values) {
    const contacts = cloneDeep(this.state.contacts);

    // in either case, need a reset
    if (!contacts[role] || !values) {
      contacts[role] = { role: contact_role_enum };
    }

    values &&
      Object.keys(values).forEach(k => {
        contacts[role][k] = values[k];
      });

    this.setState({
      hasUnsavedChanges: true,
      contacts,
    });
  }

  handleOnChange(contact_role_enum, e) {
    const [role, field] = e.target.name.split("-");

    // e === null when the operator clicks the "x" to deselect the current option
    const value = e === null ? "" : e.target.value;
    this.setStateForContactFields(contact_role_enum, role, { [field]: value });
  }

  updateSelectedReasons(items) {
    this.setState({
      ...this.state,
      hasUnsavedChanges: items.length !== 0,
      current: {
        ...this.state.current,
        reasons: items,
      },
    });
  }

  renderInputs() {
    const { loan_status, loan_decision, loan_details, reasons } = this.state.current;
    const { contacts, eave_team_options, hasUnsavedChanges } = this.state;
    const loanUnderwriterOptions = eave_team_options && eave_team_options.underwriters;

    const eaveTeamFields = [
      { name: "name" },
      { name: "address_line1" },
      { name: "phone" },
      { name: "email" },
      { name: "nmls_id" },
    ];

    return (
      <div className={styles.inputs}>
        <div className={styles.twoColumnWrapper}>
          <div>
            <h4>Loan status</h4>
            <div className={styles.select_container}>
              <Select
                placeholder="Select status…"
                clearable
                searchable
                value={loan_status}
                options={visibleLoanStatusOptions(loan_status)}
                onChange={this.onChangeSelect.bind(this, "loan_status")}
              />
              <span>
                <small>
                  Certain statuses will be set automatically by TRUS - please be certain before
                  setting a loan app to any of the states marked 'SET BY SYSTEM'.
                </small>
              </span>
            </div>
          </div>
          <div>
            <h4>Loan Underwriter</h4>
            {contacts && (
              <div className={styles.select_right_container}>
                <Contact
                  options={loanUnderwriterOptions}
                  setFields={this.setStateForContactFields}
                  selectableField="name"
                  data={contacts.loan_underwriter}
                  fields={[eaveTeamFields[0]]} // For UW Hurdle, only show the main UW Name field
                  role="loan_underwriter"
                  contact_role_enum="LoanUnderwriter"
                  handleOnChange={this.handleOnChange.bind(this)}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.twoColumnWrapper}>
          <div>
            <h4>Loan decision</h4>
            <div className={styles.select_container}>
              <Select
                placeholder="Select decision…"
                clearable
                searchable
                value={loan_decision}
                options={loanDecisions}
                onChange={this.onChangeSelect.bind(this, "loan_decision")}
              />
            </div>
          </div>
          {loan_decision === "withdrawn" && (
            <div>
              <div className={styles.select_right_container}>
                <MultiSelect
                  id="withdrawn-reasons"
                  name="withdrawn_reasons"
                  label=""
                  light
                  titleText="Withdrawn reasons"
                  initialSelectedItems={withdrawnReasonItems.filter(item => {
                    return reasons.map(r => r.id).includes(item.id);
                  })}
                  items={withdrawnReasonItems}
                  itemToString={item => (item ? item.value : "")}
                  onChange={e => {
                    this.updateSelectedReasons(e.selectedItems);
                  }}
                />
              </div>
            </div>
          )}
          {loan_decision === "suspended" && (
            <div>
              <div className={styles.select_right_container}>
                <MultiSelect
                  id="suspended-reasons"
                  name="suspended_reasons"
                  label=""
                  light
                  useTitleInItem
                  titleText="Suspended reasons"
                  initialSelectedItems={suspendedReasonItems.filter(item => {
                    return reasons.map(r => r.id).includes(item.id);
                  })}
                  items={suspendedReasonItems}
                  itemToString={item => (item ? item.value : "")}
                  onChange={e => {
                    this.updateSelectedReasons(e.selectedItems);
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <h4>
          Loan details{" "}
          <LearnMore text="These notes will be populated as the 1008's 'Underwriter Comments'" />
        </h4>
        <div className={styles.textarea_container}>
          <textarea value={loan_details || ""} onChange={this.onChangeText.bind(this)} />
        </div>
        <div className={styles.button_container}>
          <Button
            kind="primary"
            disabled={!hasUnsavedChanges}
            onClick={this.onSave.bind(this)}
            renderIcon={Save16}
          >
            {hasUnsavedChanges ? "Save loan info" : "No changes"}
          </Button>
        </div>
      </div>
    );
  }

  renderTable() {
    const { summaries } = this.state;
    return (
      <TRUSTable data={summaries}>
        <Column header="Time">
          {({ created_at }) => {
            const m = moment(created_at);
            return (
              <>
                {m.format("M/D/YYYY")} at {m.format("h:mma")}
              </>
            );
          }}
        </Column>
        <Column header="Status">
          {({ loan_status }) => <>{loan_status ? loanStatusToStr(loan_status) : EMDASH}</>}
        </Column>
        <Column header="Decision">
          {({ loan_decision }) => <>{loan_decision ? loanDecisionToStr(loan_decision) : EMDASH}</>}
        </Column>
        <Column header="Details">{({ loan_details }) => <>{loan_details || EMDASH}</>}</Column>
        <Column header="Set By User">{({ set_by_operator }) => <>{set_by_operator}</>}</Column>
      </TRUSTable>
    );
  }

  render() {
    const { errorMessage, isLoading, summaries } = this.state;

    if (isLoading) {
      return <div>Loading…</div>;
    }

    if (errorMessage) {
      return (
        <Modal
          open
          danger
          passiveModal
          modalHeading={errorMessage}
          onRequestClose={() => {
            this.setState({ errorMessage: null });
          }}
        />
      );
    }

    return (
      <div className={styles.summary}>
        <h2>Update loan info</h2>
        {this.renderInputs()}
        <h2>Loan info history (most recent first)</h2>
        {summaries && summaries.length ? this.renderTable() : <div>None yet</div>}
      </div>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(Summary);
